import {proxy} from "valtio";

import {locationsRepository} from "@/entities/locations";

import {ValidateInterface} from "@/shared/contracts/validate.interface";
import {IValtioModel} from "@/shared/models/base-model.interface";
import {Field, type FieldInstance} from "@/shared/models/form/fields";
import {MaxLengthValidator, MinLengthValidator, RequiredValidator, ZipCodeLengthByCountryValidator} from "@/shared/models/form/fields/validators";
import {Select, type SelectInstance, type SelectOption} from "@/shared/models/form/select";

import {getZipCodeBounds} from "./zip-code-by-country";

interface AddressSelectorConfig {
    isRequired?: boolean;
    initialCountry?: string;
    initialState?: string;
    initialCity?: string;
    initialAddress?: string;
    initialZipcode?: string;
}

const DEFAULT_COUNTRY = "US";

class AddressSelectorClass implements ValidateInterface, IValtioModel {
    country: SelectInstance<string, SelectOption>;
    state: SelectInstance<string, SelectOption>;
    city: FieldInstance;
    address: FieldInstance;
    zipcode: FieldInstance;

    private readonly isRequired: boolean;
    private readonly config: AddressSelectorConfig;
    private statesByCountry: Record<string, Array<SelectOption>> = {};
    private isInitializing: boolean = true;
    isLoading: boolean = true;

    constructor(config: AddressSelectorConfig = {}) {
        this.config = config;
        this.isRequired = config.isRequired ?? false;

        // Initialize country selector (empty initially, will be populated after load)
        this.country = Select<string>({
            options: [],
            initialValue: config.initialCountry,
            isRequired: this.isRequired,
            multiple: false,
        });

        // Initialize state selector (empty initially)
        this.state = Select<string>({
            options: [],
            initialValue: config.initialState,
            isRequired: this.isRequired,
            multiple: false,
            disabled: true,
        });

        const req = this.isRequired ? [new RequiredValidator()] : [];

        // Initialize city field
        this.city = Field({
            initialValue: config.initialCity || "",
            validators: [...req, new MinLengthValidator(2), new MaxLengthValidator(64)],
        });

        // Initialize address field
        this.address = Field({
            initialValue: config.initialAddress || "",
            validators: [...req, new MinLengthValidator(5), new MaxLengthValidator(128)],
        });

        // Initialize zipcode field (min/max length by selected country, per UPU)
        this.zipcode = Field({
            initialValue: config.initialZipcode || "",
            validators: [...req, new ZipCodeLengthByCountryValidator(() => getZipCodeBounds(this.selectedCountry))],
        });

        // Subscribe to country changes to update states automatically
        this.country.events.on("change", (value: string | null) => {
            // Skip during initial data load to preserve initialState
            if (!this.isInitializing) {
                void this.onCountryChange(value);
            }
        });
    }

    async init(): Promise<void> {
        try {
            const initialCountry = this.config.initialCountry || DEFAULT_COUNTRY;

            // Load countries + states for initial country (US by default)
            const {countries, states} = await locationsRepository().getCountries([initialCountry]);

            // Map to SelectOption format
            const countryOptions: Array<SelectOption> = countries.map((c) => ({
                value: c.value,
                label: c.label,
            }));

            // Cache states for initial country
            if (states[initialCountry]) {
                this.statesByCountry[initialCountry] = states[initialCountry].map((s) => ({
                    value: s.value,
                    label: s.label,
                }));
            }

            this.country.setOptions(countryOptions);

            // Set initial country
            const countryOption = this.country.findOptionByValue(initialCountry);
            if (countryOption) {
                this.country.select(countryOption);
            }

            // Load states for initial country
            const initialStates = this.statesByCountry[initialCountry] || [];
            this.state.setOptions(initialStates);
            this.state.setDisabled(initialStates.length === 0);

            // Restore initial state value if provided
            if (this.config.initialState) {
                const stateOption = this.state.findOptionByValue(this.config.initialState);
                if (stateOption) {
                    this.state.select(stateOption);
                }
            }
        } finally {
            this.isInitializing = false;
            this.isLoading = false;
        }
    }

    // Call this when country changes
    async onCountryChange(countryCode: string | null): Promise<void> {
        if (countryCode) {
            await this.updateStates(countryCode);
        } else {
            this.clearStates();
        }
        // Re-validate zipcode against new country's min/max length
        this.zipcode.validate();
    }

    private async updateStates(countryCode: string): Promise<void> {
        // Check if we already have states cached
        if (this.statesByCountry[countryCode]) {
            const states = this.statesByCountry[countryCode];
            this.state.setOptions(states);
            this.state.setDisabled(states.length === 0);
            this.state.clear();
            return;
        }

        // Fetch states from API
        try {
            this.state.setLoading(true);
            this.state.setDisabled(true);

            const {states} = await locationsRepository().getStates(countryCode);

            const stateOptions: Array<SelectOption> = states.map((s) => ({
                value: s.value,
                label: s.label,
            }));

            // Cache for future use
            this.statesByCountry[countryCode] = stateOptions;

            this.state.setOptions(stateOptions);
            this.state.setDisabled(stateOptions.length === 0);
            this.state.clear();
        } catch (error) {
            console.error(`Failed to load states for ${countryCode}:`, error);
            this.state.setOptions([]);
            this.state.setDisabled(true);
        } finally {
            this.state.setLoading(false);
        }
    }

    private clearStates(): void {
        this.state.setOptions([]);
        this.state.setDisabled(true);
        this.state.clear();
    }

    // Validation
    validate(): boolean {
        const countryResult = this.country.validate();
        const stateResult = this.state.validate();
        const cityResult = this.city.validate();
        const addressResult = this.address.validate();
        const zipcodeResult = this.zipcode.validate();

        return countryResult.isValid && stateResult.isValid && cityResult.isValid && addressResult.isValid && zipcodeResult.isValid;
    }

    get isValid(): boolean {
        return [this.country, this.state, this.city, this.address, this.zipcode].every((field) => field.isValid);
    }

    scrollIntoView(): void {
        const fields = [this.country, this.state, this.city, this.address, this.zipcode];
        const firstInvalid = fields.find((field) => !field.isValid);
        firstInvalid?.scrollIntoView();
    }

    // Get selected values
    get selectedCountry(): string | null {
        return this.country.value;
    }

    get selectedState(): string | null {
        return this.state.value;
    }

    get selectedCity(): string {
        return this.city.value;
    }

    get selectedAddress(): string {
        return this.address.value;
    }

    get selectedZipcode(): string {
        return this.zipcode.value;
    }

    // Clear all selections
    clear(): void {
        this.country.clear();
        this.clearStates();
        this.city.clear();
        this.address.clear();
        this.zipcode.clear();
    }

    /**
     * Handle autocomplete for country.
     * Loads states for the selected country so that state autocomplete can work.
     */
    async handleCountryAutocomplete(countryCode: string): Promise<void> {
        if (!countryCode) return;

        // Find and select country option
        const countryOption = this.country.findOptionByValue(countryCode);
        if (countryOption) {
            this.country.select(countryOption);
        }

        // Load states for this country (needed for state autocomplete to work)
        await this.updateStates(countryCode);
    }

    /**
     * Handle autocomplete for state.
     * Should be called after country autocomplete has loaded the states.
     */
    handleStateAutocomplete(stateCode: string): void {
        if (!stateCode) return;

        // Find and select state option
        const stateOption = this.state.findOptionByValue(stateCode);
        if (stateOption) {
            this.state.select(stateOption);
        }
    }

    /**
     * Set address from browser autocomplete data.
     * Handles the async nature of state loading after country selection.
     */
    async setFromAutocomplete(data: {country?: string; state?: string; city?: string; address?: string; zipcode?: string}): Promise<void> {
        // Set country and load states
        if (data.country) {
            await this.handleCountryAutocomplete(data.country);
        }

        // Set state (after states are loaded)
        if (data.state) {
            this.handleStateAutocomplete(data.state);
        }

        // Set other fields
        if (data.city) {
            this.city.setValue(data.city);
        }
        if (data.address) {
            this.address.setValue(data.address);
        }
        if (data.zipcode) {
            this.zipcode.setValue(data.zipcode);
        }
    }
}

export type AddressSelectorInstance = AddressSelectorClass;

export function AddressSelector(config?: AddressSelectorConfig): AddressSelectorInstance {
    return proxy(new AddressSelectorClass(config));
}
